import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { Box, Button, Typography } from "@mui/material";
import SupplierProfitability from "./Tabs/SupplierProfitability";
import CategoryProfitability from "./Tabs/CategoryProfitability";
import SkuProfitability from "./Tabs/SkuProfitability";
import ItemIdProfitability from "./Tabs/ItemIdProfitability";
import InventoryValuation from "./Tabs/InventoryValuation";
import SalesTaxReport from "./Tabs/SalesTaxReport";
import ProfitLossReport from "./Tabs/ProfitLossReport";
import { useReport } from "../../contexts/ReportContext";
import CustomCircularProgress from "../../components/Loaders/CustomCircularProgress";

const reports = {
  "supplier": {
    title: 'Supplier Profitability',
    component: SupplierProfitability
  },
  "category": {
    title: "Category Profitability",
    component: CategoryProfitability
  },
  "sku": {
    title: "SKU Profitability",
    component: SkuProfitability
  },
  'item-id': {
    title: "Item ID Profitability",
    component: ItemIdProfitability
  },
  "inventory": {
    title: "Inventory Valuation",
    component: InventoryValuation
  },
  "sales-tax": {
    title: "Sales Tax Report",
    component: SalesTaxReport
  },
  "profit-loss": {
    title: "Profit & Loss",
    component: ProfitLossReport
  }
}

function Reports() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const { isDataLoaded } = useReport();

  const pathSegments = location.pathname.split('/');
  const reportType = pathSegments[pathSegments.length - 1];

  const styles = {
    button: {
      my: 3,
      mx: 1,
      borderRadius: 20,
      display: 'block',
      px: 4,
      backgroundColor: theme.button.background,
      fontSize: 14,
      fontFamily: theme.fontFamily,
    }
  }

  if (!reports[reportType]) {
    return (
      <Box sx={{
        bgcolor: theme.background.primary,
        height: '-webkit-fill-available',
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h4" sx={{ color: theme.text.primary, mb: 2, fontFamily: theme.fontFamily }}>
          Report Not Found
        </Typography>
        <Typography variant="body1" sx={{ color: theme.text.secondary, mb: 4, fontFamily: theme.fontFamily }}>
          The requested report could not be found. Please select a valid report from the sidebar.
        </Typography>
        <Button
          variant="contained"
          sx={styles.button}
          onClick={() => navigate('/reports/supplier')}
        >
          Go to Supplier Profitability
        </Button>
      </Box>
    );
  }

  if (isDataLoaded === "PENDING") {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, bgcolor: theme.background.primary, height: '-webkit-fill-available' }}>
        <CustomCircularProgress sx={{ color: theme.color.primary }} />
      </Box>
    )
  } else if (isDataLoaded === "FAILED") {
    return <>
      <Box sx={{
        bgcolor: theme.background.primary,
        height: '-webkit-fill-available',
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h4" sx={{ color: theme.text.primary, mb: 2, fontFamily: theme.fontFamily }}>
          Reports Failed to load
        </Typography>
        <Typography variant="body1" sx={{ color: theme.text.secondary, mb: 4, fontFamily: theme.fontFamily }}>
          The requested reports could not be loaded at this time, Please try again.
        </Typography>
        <Button
          variant="contained"
          sx={styles.button}
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    </>
  }

  const { component: ReportComponent, title } = reports[reportType];

  return (
    <>
      <Helmet>
        <title>{title ?? 'Reports'} | Inventory Falcon</title>
        <meta
          name="description"
          content={`${title ?? 'Reports'} - Inventory Falcon - The best inventory management software for Walmart sellers`}
        />
      </Helmet>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 4, minHeight: "-webkit-fill-available", bgcolor: theme.background.primary }}>
        <ReportComponent />
      </Box>
    </>
  );
}

export default Reports;